import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { Disclosure } from '@/components/molecules/Disclosure';
import type { HealthOverviewParameter } from '@/domain/health';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import HealthParameterDevelopmentMetric from '../../../components/HealthParameterDevelopmentMetric/HealthParameterDevelopmentMetric';
import HealthParameterStatusMetric from '../../../components/HealthParameterStatusMetric/HealthParameterStatusMetric';
import { getHealthAlertDisplayLevel } from '../../healthAlertDisplay';
import { getParameterFishCount } from '../../healthOverviewChartMappers';
import LaksvelCompactSummaryCard from '../LaksvelCompactSummaryCard/LaksvelCompactSummaryCard';

import styles from './HealthAlertCard.module.scss';

interface HealthAlertCardProps {
    parameter: HealthOverviewParameter;
    title: string;
    expanded: boolean;
    onToggle: () => void;
    detailUrl: string | null;
    isChartLoading: boolean;
}

export default function HealthAlertCard(props: HealthAlertCardProps) {
    const { parameter, title, expanded, onToggle, detailUrl, isChartLoading } = props;
    const { t, i18n } = useTranslation('healthModule');
    const navigate = useNavigate();
    const locale = parseLocale(i18n.language);

    const fishCount = getParameterFishCount(parameter);
    const fishCountLabel =
        fishCount != null
            ? formatNumberByLocale(fishCount, locale, 0)
            : t('dashboard.notAvailable');
    const displayLevel = getHealthAlertDisplayLevel(parameter);

    return (
        <div data-testid={`health-alert-card-${parameter.key}`}>
            <Disclosure
                title={<span className={styles.title}>{title}</span>}
                open={expanded}
                onOpenChange={onToggle}
                animated
                radius="sm"
                accent={displayLevel ? 'border' : undefined}
                spacing="none"
                className={classNames(styles.card, displayLevel && styles[displayLevel])}
                headerEnd={
                    <div className={styles.metricsGroup}>
                        <HealthParameterStatusMetric parameter={parameter} variant="pill" />
                        <HealthParameterDevelopmentMetric parameter={parameter} variant="pill" />
                    </div>
                }>
                <LaksvelCompactSummaryCard
                    parameter={parameter}
                    expanded={expanded}
                    isLoading={isChartLoading}
                    fishCountLabel={fishCountLabel}
                    onNavigate={detailUrl ? () => navigate(detailUrl) : null}
                />
            </Disclosure>
        </div>
    );
}
